"use client";

import { useCallback, useMemo, useState } from "react";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";

export interface PrizeCompetition {
  competitionId: number;
  /** Normalised to `yyyy-MM-dd`. */
  date: string;
}

/**
 * Normalises a date to `yyyy-MM-dd` in local time.
 *
 * The Angular calendar compared `SameCompetitionList` values against tab labels
 * by raw string equality, which only worked because both happened to be
 * formatted the same way. Normalising both sides removes that coupling.
 */
export function toDateKey(value: string | Date | null | undefined): string {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Parses the `SameCompetitionList` payload: a JSON object string keyed by
 * competition id, e.g. `{"1919":"2026-09-11","1920":"2026-09-12"}`.
 *
 * LifestyleCompController builds this by string concatenation rather than a
 * serialiser (see its SameCompetitionList handling), so malformed values are
 * plausible. Fall back to the prize's own competition rather than throwing.
 */
export function parseSameCompetitionList(prize: LifestylePrize): PrizeCompetition[] {
  const fallback: PrizeCompetition[] = prize.CompetitionId
    ? [{ competitionId: prize.CompetitionId, date: toDateKey(prize.CompetitionEndDate) }]
    : [];

  const raw = prize.SameCompetitionList;
  if (!raw || typeof raw !== "string" || !raw.trim().startsWith("{")) {
    return fallback;
  }

  let parsed: Record<string, string>;
  try {
    parsed = JSON.parse(raw) as Record<string, string>;
  } catch {
    return fallback;
  }

  const competitions = Object.entries(parsed)
    .map(([competitionId, date]) => ({ competitionId: Number(competitionId), date: toDateKey(date) }))
    .filter(entry => Number.isFinite(entry.competitionId) && entry.competitionId > 0 && entry.date !== "");

  return competitions.length > 0 ? competitions : fallback;
}

/**
 * Daily prizes recur across consecutive one-day competitions, so a single prize
 * tile can be enterable on several dates, each a different competition id.
 *
 * This hook owns that mapping: which dates exist, which date the user has
 * picked, and which competition ids a given prize maps to on that date (which
 * is what `addTicketsForAllCompetitions` needs).
 */
export default function useDailyCompetitions(prizes: LifestylePrize[]) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  /** ContentNodeId -> the competitions that prize appears in. */
  const competitionsByPrize = useMemo(() => {
    const map = new Map<number, PrizeCompetition[]>();
    for (const prize of prizes) {
      map.set(prize.ContentNodeId, parseSameCompetitionList(prize));
    }
    return map;
  }, [prizes]);

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    competitionsByPrize.forEach(competitions => {
      competitions.forEach(({ date }) => dates.add(date));
    });
    return Array.from(dates).sort();
  }, [competitionsByPrize]);

  const competitionsForPrize = useCallback(
    (prize: LifestylePrize): PrizeCompetition[] => competitionsByPrize.get(prize.ContentNodeId) ?? [],
    [competitionsByPrize]
  );

  const availableDatesForPrize = useCallback(
    (prize: LifestylePrize): string[] => competitionsForPrize(prize).map(({ date }) => date),
    [competitionsForPrize]
  );

  /** Every competition running on a date, across all prizes. */
  const competitionIdsForDate = useCallback(
    (date: string): number[] => {
      const ids = new Set<number>();
      competitionsByPrize.forEach(competitions => {
        competitions
          .filter(competition => competition.date === date)
          .forEach(({ competitionId }) => ids.add(competitionId));
      });
      return Array.from(ids);
    },
    [competitionsByPrize]
  );

  /**
   * The competitions to enter for a prize. Scoped to the selected date when the
   * user has picked one, otherwise every date the prize runs on — which is what
   * "enter all" on a multi-day prize means.
   */
  const competitionIdsForPrize = useCallback(
    (prize: LifestylePrize): number[] => {
      const competitions = competitionsForPrize(prize);
      const scoped = selectedDate
        ? competitions.filter(competition => competition.date === selectedDate)
        : competitions;
      return scoped.map(({ competitionId }) => competitionId);
    },
    [competitionsForPrize, selectedDate]
  );

  /** Date filter predicate; passes everything while no date is selected. */
  const matchesSelectedDate = useCallback(
    (prize: LifestylePrize): boolean => {
      if (!selectedDate) return true;
      return competitionsForPrize(prize).some(competition => competition.date === selectedDate);
    },
    [competitionsForPrize, selectedDate]
  );

  return {
    availableDates,
    selectedDate,
    setSelectedDate,
    competitionsForPrize,
    availableDatesForPrize,
    competitionIdsForDate,
    competitionIdsForPrize,
    matchesSelectedDate,
  };
}
